import { useEffect, useState } from "react"
import { RGBColor } from "react-color"
import toast, { Toaster } from "react-hot-toast"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Back } from '../../components/buttons/backButton/Back'
import { VinylsColorMenu } from "../../components/vinyls/vinylsColorMenu/VinylsColorMenu"
import { VinylsEditorMenu } from "../../components/vinyls/vinylsEditorMenu/VinylsEditorMenu"
import { VinylsHintsMenu } from "../../components/vinyls/vinylsHintsMenu/VinylsHintsMenu"
import { VinylsMenu } from "../../components/vinyls/vinylsMenu/VinylsMenu"
import { MoveZone } from "../../components/zone/moveZone/MoveZone"
import { Keys } from "../../configs/keys"
import { translations } from "../../configs/translations"
import { ClientApi } from '../../services/clientApi'
import { SaveVinylRequest, VinylService } from "../../services/VinylService"
import { useStore } from "../../store/store"
import { NuiCallbacks } from '../../utils/ClientEvents'
import { rgbaToHex } from "../../utils/rgbaToHex"
import { VinylsRoulette } from "./roulette/VinylsRoulette"
import './Vinyls.css'

const CONFIG = {
    MOVE_SPEED: 2,
    ROTATE_SPEED: 0.5,
    SCALE_SPEED: 2
}

interface ActionData {
    action: 'move' | 'setMode' | 'paint' | 'add' | 'select' | 'addStencil' | 'zIndex' | 'delete'
}

interface AddAction extends ActionData {
    id: number,
    shape: 'square' | 'triangle' | 'circle' | 'star',
    x: number,
    y: number,
    size: number,
    color: string
}

interface SetModeAction extends ActionData {
    mode: 'transform' | 'scale' | 'rotate' | 'paint'
}

interface AddStencilAction extends ActionData {
    image: string
    x: number
    y: number
    size: number
}

interface SelectAction extends ActionData {
    id: number
}

interface SelectMultipleAction extends ActionData {
    ids: number[]
}

interface ChangeActionData extends ActionData {
    dx: number
    dy: number
    dr: number
    ds: number
}

interface Vinyl {
    id: number
    image: string
}

interface AdjustZIndexAction extends ActionData {
    delta: number
}

interface PaintAction extends ActionData {
    color: string
}

interface VinylGroup {
    id: number
    stickers: number[]
}

interface StickerDataDto {
    id: number,
    t: number,
    x: number,
    y: number,
    sx: number,
    sy: number,
    r: number,
    c: string,
    z: number,
}

export const Vinyls = () => {

    const [searchParams] = useSearchParams()
    const carId = Number(searchParams.get('carId'))
    const language = useStore(state => state.language) as keyof typeof translations
    const t = translations[language]

    const [basePrice, setBasePrice] = useState<number | null>(null)
    const [totalPrice, setTotalPrice] = useState<number | null>(null)
    const [pricePerSticker, setPricePerSticker] = useState<number | null>(null)

    const [stickersData, setStickersData] = useState<StickerDataDto[] | null>(null)

    const [data, setData] = useState<string | null>(null)
    const [stickers, setStickers] = useState<Vinyl[]>([])
    const [selectedStickers, setSelectedStickers] = useState<number[]>([])
    const [groups, setGroups] = useState<VinylGroup[]>([])

    const [selectedEditorMode, setSelectedEditorMode] = useState<'Q' | 'W' | 'E' | 'R'>('Q')

    const [stickerIdCounter, setStickerIdCounter] = useState(1)

    useEffect(() => {
        const audio = new Audio("./sounds/menu_sound_1.wav")
        audio.volume = 0.1
        audio.play()
    }, [selectedEditorMode])

    const editorModesKeys: {
        mode: string,
        keys: string[]
    }[] = [
            { mode: 'Q', keys: Keys.Q },
            { mode: 'W', keys: Keys.W },
            { mode: 'E', keys: Keys.E },
            { mode: 'R', keys: Keys.R }
        ]

    const [shiftKeyPressed, setShiftKeyPressed] = useState(false)
    const [ctrlKeyPressed, setCtrlKeyPressed] = useState(false)

    const [isHintShown, setIsHintShown] = useState(true)
    const [hindSlideOutAnimationStarted, setHindSlideOutAnimationStarted] = useState(false)


    const [color, setColor] = useState<RGBColor>({ r: 255, g: 255, b: 255, a: 1 })
    const [showColorMenu, setShowColorMenu] = useState(false)
    const [vinylsMenuSlideOutAnimationStarted, setVinylsMenuSlideOutAnimationStarted] = useState(false)
    const [vinylsColorMenuSlideOutAnimationStarted, setVinylsColorMenuSlideOutAnimationStarted] = useState(false)

    const sendAction = (data: ActionData) => {
        ClientApi.send(NuiCallbacks.EDIT_VINYL, data)
    }

    const getChangingSpeed = (speed: number) => {
        if (shiftKeyPressed) {
            return speed * 2
        }

        if (ctrlKeyPressed) {
            return speed / 2
        }

        return speed
    }

    const dKey = Keys.D

    const changingActionKeys: { key: string, sendAction: () => void }[] = [
        {
            key: "ArrowUp",
            sendAction: () => {
                const data: ChangeActionData = {
                    action: "move",
                    dx: selectedEditorMode === 'Q' ? getChangingSpeed(CONFIG.MOVE_SPEED) : 0,
                    dy: 0,
                    dr: 0,
                    ds: selectedEditorMode === 'E' ? getChangingSpeed(CONFIG.SCALE_SPEED) : 0
                }

                sendAction(data)
            }
        },
        {
            key: "ArrowDown",
            sendAction: () => {
                const data: ChangeActionData = {
                    action: "move",
                    dx: selectedEditorMode === 'Q' ? -getChangingSpeed(CONFIG.MOVE_SPEED) : 0,
                    dy: 0,
                    dr: 0,
                    ds: selectedEditorMode === 'E' ? -getChangingSpeed(CONFIG.SCALE_SPEED) : 0
                }

                sendAction(data)
            }
        },
        {
            key: "ArrowLeft",
            sendAction: () => {
                const data: ChangeActionData = {
                    action: "move",
                    dx: 0,
                    dy: selectedEditorMode === 'Q' ? -getChangingSpeed(CONFIG.MOVE_SPEED) : 0,
                    dr: selectedEditorMode === 'W' ? -getChangingSpeed(CONFIG.ROTATE_SPEED) : 0,
                    ds: 0
                }

                sendAction(data)
            }
        },
        {
            key: "ArrowRight",
            sendAction: () => {
                const data: ChangeActionData = {
                    action: "move",
                    dx: 0,
                    dy: selectedEditorMode === 'Q' ? getChangingSpeed(CONFIG.MOVE_SPEED) : 0,
                    dr: selectedEditorMode === 'W' ? getChangingSpeed(CONFIG.ROTATE_SPEED) : 0,
                    ds: 0
                }

                sendAction(data)
            }
        },
        {
            key: '+',
            sendAction: () => {
                const data: AdjustZIndexAction = {
                    action: "zIndex",
                    delta: 1
                }

                sendAction(data)
            }
        },
        {
            key: '-',
            sendAction: () => {
                const data: AdjustZIndexAction = {
                    action: "zIndex",
                    delta: -1
                }

                sendAction(data)
            }
        }

    ]

    const modificatorKeys: { key: string, setKeyPressed: (keyPressed: boolean) => void }[] = [
        { key: "Shift", setKeyPressed: setShiftKeyPressed },
        { key: "Control", setKeyPressed: setCtrlKeyPressed },
    ]

    const zKey = Keys.Z
    const jKey = Keys.J
    const aKey = Keys.A

    const handleKeyPress = (key: string) => {

        editorModesKeys.forEach((editorMode) => {
            if (editorMode.keys.includes(key)) {
                setSelectedEditorMode(editorMode.mode as 'Q' | 'W' | 'E' | 'R')

                const mapToEditorMode = (localMode: string) => {
                    switch (localMode) {
                        case "Q":
                            return "transform"
                        case "W":
                            return "rotate"
                        case "E":
                            return "scale"
                        case "R":
                            return "paint"
                        default:
                            return "transform"
                    }
                }

                const data: SetModeAction = {
                    action: "setMode",
                    mode: mapToEditorMode(editorMode.mode)
                }
                sendAction(data)
            }
        })

        changingActionKeys.forEach((action) => {
            if (action.key === key) {
                action.sendAction()
            }
        })

        modificatorKeys.forEach((modificatorKey) => {
            if (modificatorKey.key === key) {
                modificatorKey.setKeyPressed(true)
            }
        })

        if (zKey.includes(key) && shiftKeyPressed) {
            if (isHintShown) {
                const audio = new Audio("./sounds/woosh_1.wav")
                audio.volume = 0.05
                audio.play()
                setHindSlideOutAnimationStarted(true)
                setTimeout(() => {
                    setIsHintShown(false)
                }, 400)
            } else {
                const audio = new Audio("./sounds/woosh_1_reversed.wav")
                audio.volume = 0.1
                audio.play()
                setIsHintShown(true)
                setHindSlideOutAnimationStarted(false)
            }
        }

        if (dKey.includes(key)) {
            const data: ActionData = {
                action: "delete"
            }
            if (shiftKeyPressed) {
                const selectAllData: SelectMultipleAction = {
                    action: "select",
                    ids: stickers.map((sticker) => sticker.id)
                }
                sendAction(selectAllData)
                const deleteData: ActionData = {
                    action: "delete"
                }
                sendAction(deleteData)
                setStickers([])
                setSelectedStickers([])
                return
            }
            setStickers(stickers.filter((sticker) => !selectedStickers.includes(sticker.id)))
            setSelectedStickers([])
            sendAction(data)
        }

        if (jKey.includes(key)) {
            if (shiftKeyPressed) {
                const newGroups = groups
                selectedStickers.forEach((stickerId) => {
                    const group = groups.find((group) => group.stickers.includes(stickerId))
                    if (group) {
                        newGroups.splice(newGroups.indexOf(group), 1)
                    }
                })
                setGroups(newGroups)
                setSelectedStickers([])
                return
            }

            const newGroup = {
                id: groups.length + 1,
                stickers: selectedStickers
            }
            setGroups([...groups, newGroup])
        }

        if (aKey.includes(key)) {
            if (shiftKeyPressed) {
                setSelectedStickers(stickers.map((sticker) => sticker.id))
                const selectAllData: SelectMultipleAction = {
                    action: "select",
                    ids: stickers.map((sticker) => sticker.id)
                }
                sendAction(selectAllData)
            }
        }

    }

    const handleKeyRelease = (key: string) => {
        modificatorKeys.forEach((modificatorKey) => {
            if (modificatorKey.key === key) {
                modificatorKey.setKeyPressed(false)
            }
        })
    }

    const handleStickerSelect = (stickerId: number) => {
        const newSelectedStickers: number[] = []

        if (groups.some((group) => group.stickers.includes(stickerId))) {
            newSelectedStickers.push(...groups.find((group) => group.stickers.includes(stickerId))?.stickers || [])
        } else {
            newSelectedStickers.push(stickerId)
        }


        if (shiftKeyPressed) {
            newSelectedStickers.push(...selectedStickers)
            setSelectedStickers(newSelectedStickers)
            const data: SelectMultipleAction = {
                action: "select",
                ids: newSelectedStickers
            }
            sendAction(data)
        } else {
            setSelectedStickers(newSelectedStickers)
            const data: SelectMultipleAction = {
                action: "select",
                ids: newSelectedStickers
            }
            sendAction(data)
        }
    }

    const handleAddAction = (shape: 'square' | 'triangle' | 'circle' | 'star') => {
        const data: AddAction = {
            id: stickerIdCounter,
            action: "add",
            shape: shape,
            x: 500,
            y: 1250,
            size: 60,
            color: "#aaaaaa"
        }
        sendAction(data)
        setStickerIdCounter(prev => prev + 1)
    }

    const handleAddStencilAction = (base64Image: string) => {
        const data: AddStencilAction = {
            action: "addStencil",
            image: base64Image,
            x: 400,
            y: 150,
            size: 60,
        }
        sendAction(data)
    }

    useEffect(() => {
        const wooshSound = new Audio("./sounds/woosh_1.wav")
        wooshSound.volume = 0.01
        const wooshSoundReversed = new Audio("./sounds/woosh_1_reversed.wav")
        wooshSoundReversed.volume = 0.01
        if (selectedEditorMode === "R" && !showColorMenu) {
            wooshSound.play()
            setVinylsMenuSlideOutAnimationStarted(true)
            setTimeout(() => {
                setShowColorMenu(true)
                setVinylsMenuSlideOutAnimationStarted(false)
                wooshSoundReversed.play()
            }, 400)
        } else if (selectedEditorMode !== "R" && showColorMenu) {
            wooshSound.play()
            setVinylsColorMenuSlideOutAnimationStarted(true)
            setTimeout(() => {
                setShowColorMenu(false)
                setVinylsColorMenuSlideOutAnimationStarted(false)
                wooshSoundReversed.play()
            }, 400)
        }
    }, [selectedEditorMode])

    const [isPaintRequestSent, setIsPaintRequestSent] = useState(false)
    useEffect(() => {
        if (!isPaintRequestSent) {
            if (!selectedStickers.length) return
            const selectData: SelectMultipleAction = {
                action: "select",
                ids: selectedStickers
            }
            sendAction(selectData)
            const paintData: PaintAction = {
                action: "paint",
                color: rgbaToHex(color),
            }
            sendAction(paintData)
            setIsPaintRequestSent(true)
            setTimeout(() => {
                setIsPaintRequestSent(false)
            }, 100)
        }
    }, [color])

    const handleSelectGroup = (groupId: number) => {
        setSelectedStickers(groups?.find((group) => group.id === groupId)?.stickers || [])
        const selectData: SelectMultipleAction = {
            action: "select",
            ids: groups?.find((group) => group.id === groupId)?.stickers || []
        }
        sendAction(selectData)
    }

    const navigate = useNavigate()
    const handleBuyEvent = async () => {
        try {
            const data = await ClientApi.request<StickerDataDto[]>(
                NuiCallbacks.REQUEST_VINYL_DATA,
                {}
            )
            console.log('data', data?.length)
            if (!data) return
            const request: SaveVinylRequest = {
                vehicleId: carId,
                name: "",
                stickers: data.map((sticker) => ({
                    id: sticker.id,
                    t: sticker.t,
                    x: sticker.x,
                    y: sticker.y,
                    sx: sticker.sx,
                    sy: sticker.sy,
                    r: sticker.r,
                    c: sticker.c,
                    z: sticker.z
                })),
                groups: groups.map((group) => ({
                    id: group.id,
                    name: `Group ${group.id}`,
                    stickerIds: group.stickers.map((sticker) => sticker)
                })) || []
            }
            console.log('Received data:', data, request.groups)
            await VinylService.saveVinyl(request)
                .then(() => {
                    navigate('/tuning?carId=' + carId)
                })
                .catch((error) => {
                    toast.error(
                        error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                            ? t[error.response.data as keyof typeof t]
                            : t.error_unknown
                    )
                })
        } catch (error) {
            console.error('Error fetching data:', error)
        }
    }

    const [setEditorHtmlPageRequestSent, setSetEditorHtmlPageRequestSent] = useState(false)
    useEffect(() => {
        if (!setEditorHtmlPageRequestSent) {
            setSetEditorHtmlPageRequestSent(true)
            const VINYL_LOCAL_ID_WHEN_CREATE = -1
            VinylService.requestSetVinylEditorHtmlPage(carId, VINYL_LOCAL_ID_WHEN_CREATE)
                .catch((error) => {
                    toast.error(
                        error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                            ? t[error.response.data as keyof typeof t]
                            : t.error_unknown,
                        { className: 'notification-error' }
                    )
                })
        }
    }, [])

    const [vinylDataRequestSent, setVinylDataRequestSent] = useState(false)
    useEffect(() => {
        if (!vinylDataRequestSent) {
            setVinylDataRequestSent(true)
            VinylService.getVinylData(carId, -1)
                .then((response) => {
                    setBasePrice(response.price)
                    setPricePerSticker(response.pricePerSticker)
                    setTotalPrice(response.price + response.pricePerSticker * stickers.length)
                })
                .catch((error) => {
                    toast.error(
                        error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                            ? t[error.response.data as keyof typeof t]
                            : t.error_unknown,
                        { className: 'notification-error' }
                    )
                })
        }
    }, [])

    useEffect(() => {
        if (pricePerSticker && basePrice) {
            setTotalPrice(basePrice + stickers.length * pricePerSticker)
        }
    }, [stickers])

    return (
        <div
            className="vinyls"
            tabIndex={0}
            onKeyDown={(e) => { handleKeyPress(e.key) }}
            onKeyUp={(e) => { handleKeyRelease(e.key) }}
        >
            <Back
                containerStyles={{
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    padding: '20px',
                    color: '#000',
                    gap: '10px',
                }}
                imgStyle={{
                    filter: 'brightness(0)',
                }}
                onClick={() => navigate(`/tuning?carId=${carId}`)}
            />

            <VinylsEditorMenu
                selectedEditorMode={selectedEditorMode}
                setSelectedEditorMode={setSelectedEditorMode}
            />

            {isHintShown &&
                <div className={`vinyls-hints-menu-slide-in ${hindSlideOutAnimationStarted ? "vinyls-hints-menu-slide-out" : ""}`}>
                    <VinylsHintsMenu />
                </div>
            }

            {
                showColorMenu ?
                    (<div className={`vinyls-menu-slide-in ${vinylsColorMenuSlideOutAnimationStarted ? "vinyls-menu-slide-out" : ""}`}>
                        <VinylsColorMenu
                            color={color}
                            setColor={setColor}
                        />
                    </div>)
                    :
                    (<div className={`vinyls-menu-slide-in ${vinylsMenuSlideOutAnimationStarted ? "vinyls-menu-slide-out" : ""}`}>
                        <VinylsMenu
                            stickers={stickers}
                            setStickers={setStickers}
                            sendAddStickerAction={(shape: 'square' | 'triangle' | 'circle' | 'star') => handleAddAction(shape)}
                            sendAddStencilAction={(base64Image: string) => handleAddStencilAction(base64Image)}
                            stickerIdCounter={stickerIdCounter}
                            buyStickerAction={handleBuyEvent}
                            price={totalPrice}
                        />
                    </div>)
            }

            <MoveZone carId={carId} />

            <VinylsRoulette
                items={stickers.map((sticker) => {
                    return {
                        name: `ID: ${sticker.id}`,
                        image: sticker.image,
                        onClick: () => {
                            handleStickerSelect(sticker.id)
                        },
                        id: sticker.id
                    }
                })}
                selectedStickers={selectedStickers}
                groups={groups}
                selectGroup={(groupId: number) => {
                    handleSelectGroup(groupId)
                }}
            />

            <Toaster
                position="bottom-center"
            />
        </div>
    )
}